import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import { bookingService } from '../services/api';
import '../styles/BookingForm.css';

function BookingForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
    adresse: '',
    plz: '',
    ort: '',
    land: 'Deutschland',
    paketId: '',
    eventdatum: '',
    versanddatum: '',
    ruecksendungAb: '',
    reservierungAblauf: '',
    preis: '',
    status: 'Neu',
    zahlungsstatus: 'Offen',
    notizen: ''
  });

  useEffect(() => {
    if (isEdit) {
      loadBooking();
    }
  }, [id]);

  const loadBooking = async () => {
    try {
      setLoading(true);
      const response = await bookingService.getById(id);
      const booking = response.booking;
      
      setFormData({
        vorname: booking.Vorname || '',
        nachname: booking.Nachname || '',
        email: booking['E-Mail'] || '',
        telefon: booking.Telefon || '',
        adresse: booking.Adresse || '',
        plz: booking.PLZ || '',
        ort: booking.Ort || '',
        land: booking.Land || 'Deutschland',
        paketId: booking.Paket_ID || '',
        eventdatum: booking.Eventdatum ? booking.Eventdatum.split('T')[0] : '',
        versanddatum: booking.Versanddatum ? booking.Versanddatum.split('T')[0] : '',
        ruecksendungAb: booking.Ruecksendung_ab ? booking.Ruecksendung_ab.split('T')[0] : '',
        reservierungAblauf: booking.Reservierung_ablauf ? booking.Reservierung_ablauf.split('T')[0] : '',
        preis: booking.Preis || '',
        status: booking.Status || 'Neu',
        zahlungsstatus: booking.Zahlungsstatus || 'Offen',
        notizen: booking.Notizen || ''
      });
    } catch (error) {
      console.error('Error loading booking:', error);
      alert('Fehler beim Laden der Buchung');
      navigate('/feierpakete');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit) {
        await bookingService.update(id, formData);
      } else {
        await bookingService.create(formData);
      }
      navigate('/feierpakete');
    } catch (error) {
      console.error('Error saving booking:', error);
      alert('Fehler beim Speichern: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="booking-form-page">
      <div className="page-header">
        <div>
          <Link to={isEdit ? `/feierpakete/${id}` : '/feierpakete'} className="back-button">
            <FaArrowLeft /> Zurück
          </Link>
          <h1>📦 {isEdit ? 'Buchung bearbeiten' : 'Neue Buchung'}</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="booking-form">
        <div className="card">
          <h2>Kundendaten</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="vorname">Vorname *</label>
              <input
                type="text"
                id="vorname"
                name="vorname"
                className="form-input"
                value={formData.vorname}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="nachname">Nachname *</label>
              <input
                type="text"
                id="nachname"
                name="nachname"
                className="form-input"
                value={formData.nachname}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">E-Mail *</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="telefon">Telefon</label>
              <input
                type="tel"
                id="telefon"
                name="telefon"
                className="form-input"
                value={formData.telefon}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="adresse">Adresse</label>
            <input
              type="text"
              id="adresse"
              name="adresse"
              className="form-input"
              value={formData.adresse}
              onChange={handleChange}
              placeholder="Straße und Hausnummer"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="plz">PLZ</label>
              <input
                type="text"
                id="plz"
                name="plz"
                className="form-input"
                value={formData.plz}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="ort">Ort</label>
              <input
                type="text"
                id="ort"
                name="ort"
                className="form-input"
                value={formData.ort}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="land">Land</label>
              <input
                type="text"
                id="land"
                name="land"
                className="form-input"
                value={formData.land}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h2>Buchungsdetails</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="paketId">Paket-ID</label>
              <input
                type="text"
                id="paketId"
                name="paketId"
                className="form-input"
                value={formData.paketId}
                onChange={handleChange}
                placeholder="z.B. HBF0001"
              />
            </div>

            <div className="form-group">
              <label htmlFor="preis">Preis (€)</label>
              <input
                type="number"
                id="preis"
                name="preis"
                className="form-input"
                value={formData.preis}
                onChange={handleChange}
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                className="form-select"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="Neu">Neu</option>
                <option value="Angefragt">Angefragt</option>
                <option value="Bestätigt">Bestätigt</option>
                <option value="Bezahlt">Bezahlt</option>
                <option value="Versendet">Versendet</option>
                <option value="Zurück">Zurück</option>
                <option value="Abgeschlossen">Abgeschlossen</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="zahlungsstatus">Zahlungsstatus</label>
              <select
                id="zahlungsstatus"
                name="zahlungsstatus"
                className="form-select"
                value={formData.zahlungsstatus}
                onChange={handleChange}
              >
                <option value="Offen">Offen</option>
                <option value="Bezahlt">Bezahlt</option>
                <option value="Teilzahlung">Teilzahlung</option>
                <option value="Storniert">Storniert</option>
              </select>
            </div>
          </div>
        </div>

        <div className="card">
          <h2>Termine</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="eventdatum">Eventdatum</label>
              <input
                type="date"
                id="eventdatum"
                name="eventdatum"
                className="form-input"
                value={formData.eventdatum}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="reservierungAblauf">Reservierung bis</label>
              <input
                type="date"
                id="reservierungAblauf"
                name="reservierungAblauf"
                className="form-input"
                value={formData.reservierungAblauf}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="versanddatum">Versanddatum</label>
              <input
                type="date"
                id="versanddatum"
                name="versanddatum"
                className="form-input"
                value={formData.versanddatum}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="ruecksendungAb">Rücksendung ab</label>
              <input
                type="date"
                id="ruecksendungAb"
                name="ruecksendungAb"
                className="form-input"
                value={formData.ruecksendungAb}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h2>Notizen</h2>
          <div className="form-group">
            <textarea
              id="notizen"
              name="notizen"
              className="form-textarea"
              value={formData.notizen}
              onChange={handleChange}
              rows="4"
              placeholder="Zusätzliche Informationen zur Buchung..."
            />
          </div>
        </div>

        <div className="form-actions">
          <Link to={isEdit ? `/feierpakete/${id}` : '/feierpakete'} className="btn btn-secondary">
            Abbrechen
          </Link>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            <FaSave />
            {saving ? 'Wird gespeichert...' : (isEdit ? 'Änderungen speichern' : 'Buchung erstellen')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default BookingForm;
